import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HRReports.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const CITIES = ['All Cities', 'Lahore', 'Karachi', 'Islamabad', 'Multan'];

function formatPKR(amount) {
  if (!amount && amount !== 0) return '—';
  return `PKR ${Number(amount).toLocaleString('en-PK')}`;
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarClass(name) {
  const c = ['rp-av-blue','rp-av-teal','rp-av-amber','rp-av-purple','rp-av-coral','rp-av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

// Short display form of a 'YYYY-MM-DD' trigger date, e.g. "Oct 4"
function formatTriggerDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
}

// Compact one-line summary of a row's late-policy triggers for the Salary
// Report table, e.g. "Oct 4 (₨500), Oct 9 (casual)"
function triggerSummary(triggers) {
  if (!triggers || triggers.length === 0) return null;
  return triggers.map(t =>
    t.absorbed_by === 'salary'
      ? `${formatTriggerDate(t.date)} (₨${t.amount_pkr})`
      : `${formatTriggerDate(t.date)} (casual)`
  ).join(', ');
}

// "Absent (Late)": the days the late policy charged to SALARY (3rd/6th/9th...
// late that casual leave did NOT absorb). Shown beside real absences, never
// added into them. Display only — salary maths and the attendance table are
// untouched, so nothing is double-counted. Casual-absorbed triggers are not
// included because they cost no pay.
const lateAbsent = (r) => r.late_deduction_days || 0;

function exportToCSV(reports, month, year) {
  const monthName = MONTHS[month - 1];
  const headers = [
    'Employee ID','Full Name','Location','City',
    'Base Salary (PKR)','Days Present','Days Absent','Absent Due to Late',
    'Late Count','Absent Deduction (PKR)','Late Deduction (PKR)',
    'Casual Leaves Used','Approved Leaves',
    'Total Deduction (PKR)','Net Salary (PKR)','Deduction %',
    'Late Policy Trigger Dates'
  ];

  const rows = reports.map(r => [
    r.emp_code, r.employee_name, r.location_name || '', r.city || '',
    r.base_salary, r.working_days_present, r.absent_days, lateAbsent(r),
    r.late_count, r.absent_deduction, r.late_deduction,
    r.casual_leaves_consumed, r.approved_leaves,
    r.total_deduction, r.net_salary, r.deduction_percent + '%',
    triggerSummary(r.late_policy_triggers) || ''
  ]);

  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Fotile_Salary_Report_${monthName}_${year}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function HRReports() {
  const now = new Date();
  const [month, setMonth]       = useState(now.getMonth() + 1);
  const [year, setYear]         = useState(now.getFullYear());
  const [cityFilter, setCityFilter] = useState('All Cities');
  const [reports, setReports]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [activeTab, setActiveTab] = useState('salary');
  const [sortKey, setSortKey]   = useState('employee_name');
  const [sortDir, setSortDir]   = useState('asc');

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getAllSalaryReports({
        month, year,
        city: cityFilter !== 'All Cities' ? cityFilter : undefined,
      });
      setReports(res.reports || []);
      setSummary(res.summary || null);
    } catch (err) {
      setError('Failed to load reports. Try again.');
    } finally {
      setLoading(false);
    }
  }, [month, year, cityFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // Sort reports
  const sorted = [...reports].sort((a, b) => {
    let av = a[sortKey] ?? '';
    let bv = b[sortKey] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Late report — sorted by late count desc
  const lateReport = [...reports]
    .filter(r => r.late_count > 0)
    .sort((a, b) => b.late_count - a.late_count);

  const years = [];
  for (let y = now.getFullYear(); y >= 2024; y--) years.push(y);

  return (
    <div className="rp-root">

      {/* Header */}
      <div className="rp-header">
        <div className="rp-header-left">
          <h1 className="rp-title">Reports</h1>
          <span className="rp-subtitle">
            {MONTHS[month - 1]} {year}
            {summary ? ` · ${summary.total_employees} employees` : ''}
          </span>
        </div>
        <div className="rp-header-right">
          {/* Month selector */}
          <select
            className="rp-select"
            value={month}
            onChange={e => setMonth(parseInt(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          {/* Year selector */}
          <select
            className="rp-select"
            value={year}
            onChange={e => setYear(parseInt(e.target.value))}
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {/* City filter */}
          <select
            className="rp-select"
            value={cityFilter}
            onChange={e => setCityFilter(e.target.value)}
          >
            {CITIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {/* Export */}
          <button
            className="rp-export-btn"
            onClick={() => exportToCSV(sorted, month, year)}
            disabled={reports.length === 0}
          >
            ↓ Export CSV
          </button>
        </div>
      </div>

      {error && <div className="rp-error">{error}</div>}

      {/* Summary cards */}
      {summary && (
        <div className="rp-summary">
          <div className="rp-sum-card">
            <span className="rp-sum-num">{summary.total_employees}</span>
            <span className="rp-sum-lbl">Employees</span>
          </div>
          <div className="rp-sum-card rp-sum-red">
            <span className="rp-sum-num">{formatPKR(summary.total_deductions)}</span>
            <span className="rp-sum-lbl">Total Deductions</span>
          </div>
          <div className="rp-sum-card rp-sum-green">
            <span className="rp-sum-num">{formatPKR(summary.total_net_salary)}</span>
            <span className="rp-sum-lbl">Total Net Salary</span>
          </div>
          <div className="rp-sum-card rp-sum-amber">
            <span className="rp-sum-num">
              {reports.reduce((s, r) => s + (r.late_count || 0), 0)}
            </span>
            <span className="rp-sum-lbl">Total Lates</span>
          </div>
          <div className="rp-sum-card rp-sum-slate">
            <span className="rp-sum-num">
              {reports.reduce((s, r) => s + (r.absent_days || 0), 0)}
            </span>
            <span className="rp-sum-lbl">Total Absences</span>
          </div>
          <div className="rp-sum-card rp-sum-red">
            <span className="rp-sum-num">
              {reports.reduce((s, r) => s + lateAbsent(r), 0)}
            </span>
            <span className="rp-sum-lbl">Absent (Late)</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="rp-tabs">
        <button
          className={`rp-tab ${activeTab === 'salary' ? 'rp-tab-active' : ''}`}
          onClick={() => setActiveTab('salary')}
        >
          Salary Report
        </button>
        <button
          className={`rp-tab ${activeTab === 'late' ? 'rp-tab-active' : ''}`}
          onClick={() => setActiveTab('late')}
        >
          Late Report
          {lateReport.length > 0 && (
            <span className="rp-tab-badge">{lateReport.length}</span>
          )}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="rp-loading">
          <div className="rp-spinner" />
          <p>Calculating reports…</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="rp-empty">
          No data found for {MONTHS[month - 1]} {year}.
        </div>
      ) : activeTab === 'salary' ? (

        /* ── SALARY TABLE ── */
        <div className="rp-table-wrap">
          <table className="rp-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('employee_name')} className="rp-sortable">
                  Employee{sortIcon('employee_name')}
                </th>
                <th onClick={() => handleSort('location_name')} className="rp-sortable">
                  Location{sortIcon('location_name')}
                </th>
                <th onClick={() => handleSort('base_salary')} className="rp-sortable rp-num">
                  Base Salary{sortIcon('base_salary')}
                </th>
                <th onClick={() => handleSort('working_days_present')} className="rp-sortable rp-num">
                  Present{sortIcon('working_days_present')}
                </th>
                <th onClick={() => handleSort('absent_days')} className="rp-sortable rp-num">
                  Absent{sortIcon('absent_days')}
                </th>
                <th onClick={() => handleSort('late_deduction_days')} className="rp-sortable rp-num">
                  Absent (Late){sortIcon('late_deduction_days')}
                </th>
                <th onClick={() => handleSort('late_count')} className="rp-sortable rp-num">
                  Lates{sortIcon('late_count')}
                </th>
                <th className="rp-num">Absent Ded.</th>
                <th className="rp-num">Late Ded.</th>
                <th onClick={() => handleSort('total_deduction')} className="rp-sortable rp-num">
                  Total Ded.{sortIcon('total_deduction')}
                </th>
                <th onClick={() => handleSort('net_salary')} className="rp-sortable rp-num">
                  Net Salary{sortIcon('net_salary')}
                </th>
                <th className="rp-num">Ded. %</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, idx) => {
                const triggers = r.late_policy_triggers || [];
                const salaryTriggers = triggers.filter(t => t.absorbed_by === 'salary');
                const casualTriggers = triggers.filter(t => t.absorbed_by === 'casual');
                return (
                  <tr key={idx} className={parseFloat(r.total_deduction) > 0 ? 'rp-row-deducted' : ''}>
                    <td>
                      <div className="rp-emp-cell">
                        <div className={`rp-avatar ${getAvatarClass(r.employee_name)}`}>
                          {getInitials(r.employee_name)}
                        </div>
                        <div className="rp-emp-info">
                          <span className="rp-emp-name">{r.employee_name}</span>
                          <span className="rp-emp-code">{r.emp_code}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="rp-loc-cell">
                        <span className="rp-loc-name">{r.location_name || '—'}</span>
                        <span className="rp-city-tag">{r.city || ''}</span>
                      </div>
                    </td>
                    <td className="rp-num">{formatPKR(r.base_salary)}</td>
                    <td className="rp-num rp-present">{r.working_days_present ?? '—'}</td>
                    <td className="rp-num">
                      <span className={r.absent_days > 0 ? 'rp-bad' : 'rp-good'}>
                        {r.absent_days}
                      </span>
                    </td>
                    <td className="rp-num">
                      <span className={lateAbsent(r) > 0 ? 'rp-bad' : 'rp-good'}>
                        {lateAbsent(r)}
                      </span>
                    </td>
                    <td className="rp-num">
                      <span className={r.late_count > 0 ? 'rp-warn' : 'rp-good'}>
                        {r.late_count}
                      </span>
                    </td>
                    <td className="rp-num rp-deduct">{formatPKR(r.absent_deduction)}</td>
                    <td className="rp-num rp-deduct">
                      {formatPKR(r.late_deduction)}
                      {/* Late-policy trigger dates — tells HR exactly which day(s)
                          the half/full-day mark landed on, and whether casual
                          absorbed it or it hit salary. Resets every month since
                          this comes straight from that month's own calculation. */}
                      {triggers.length > 0 && (
                        <div style={{ marginTop: 3, fontWeight: 400 }}>
                          {salaryTriggers.length > 0 && (
                            <div style={{ fontSize: 10, color: '#b91c1c' }}>
                              {triggerSummary(salaryTriggers)}
                            </div>
                          )}
                          {casualTriggers.length > 0 && (
                            <div style={{ fontSize: 10, color: '#4338ca' }}>
                              {triggerSummary(casualTriggers)}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="rp-num">
                      <span className={parseFloat(r.total_deduction) > 0 ? 'rp-bad' : ''}>
                        {formatPKR(r.total_deduction)}
                      </span>
                    </td>
                    <td className="rp-num rp-net">{formatPKR(r.net_salary)}</td>
                    <td className="rp-num">
                      <span className={`rp-pct ${parseFloat(r.deduction_percent) > 0 ? 'rp-pct-bad' : 'rp-pct-good'}`}>
                        {r.deduction_percent}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      ) : (

        /* ── LATE REPORT ── */
        <div className="rp-late-wrap">
          {lateReport.length === 0 ? (
            <div className="rp-empty">
              ✓ No late arrivals recorded for {MONTHS[month - 1]} {year}.
            </div>
          ) : (
            <>
              <div className="rp-late-legend">
                Employees ranked by late count — most lates at top.
                Policy (from Oct 2026): every 3rd late = 0.5 day deduction (3rd, 6th, 9th...)
              </div>
              {lateReport.map((r, idx) => {
                const pct = Math.min((r.late_count / 6) * 100, 100);
                const isWarning = r.late_count >= 3;
                const isDanger  = r.late_count >= 6;
                const triggers  = r.late_policy_triggers || [];
                return (
                  <div key={idx} className={`rp-late-card ${isDanger ? 'rp-late-danger' : isWarning ? 'rp-late-warning' : ''}`}>
                    <div className="rp-late-rank">#{idx + 1}</div>
                    <div className={`rp-avatar ${getAvatarClass(r.employee_name)}`}>
                      {getInitials(r.employee_name)}
                    </div>
                    <div className="rp-late-info">
                      <span className="rp-late-name">{r.employee_name}</span>
                      <span className="rp-late-meta">
                        {r.emp_code} · {r.location_name || '—'} · {r.city || '—'}
                      </span>
                      <div className="rp-late-bar-wrap">
                        <div className="rp-late-bar">
                          <div
                            className={`rp-late-fill ${isDanger ? 'rp-fill-danger' : isWarning ? 'rp-fill-warning' : 'rp-fill-ok'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="rp-late-bar-label">{r.late_count} / 6</span>
                      </div>
                      {/* Exact dates the 3rd/6th-late half/full-day mark landed,
                          and whether it was absorbed by casual or hit salary. */}
                      {triggers.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                          {triggers.map((t, ti) => (
                            <span key={ti} style={{
                              display: 'inline-block', marginRight: 6, marginTop: 2,
                              padding: '1px 7px', borderRadius: 999,
                              background: t.absorbed_by === 'salary' ? '#fee2e2' : '#e0e7ff',
                              color: t.absorbed_by === 'salary' ? '#b91c1c' : '#4338ca',
                              fontWeight: 600,
                            }}>
                              {formatTriggerDate(t.date)} · {t.label === 'full_day' ? 'Full' : 'Half'}
                              {t.absorbed_by === 'salary' ? ` · ₨${t.amount_pkr}` : ' · Casual'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="rp-late-stats">
                      <div className="rp-late-stat">
                        <span className={`rp-late-count ${isDanger ? 'rp-bad' : isWarning ? 'rp-warn' : ''}`}>
                          {r.late_count}
                        </span>
                        <span className="rp-late-stat-lbl">Lates</span>
                      </div>
                      <div className="rp-late-stat">
                        <span className="rp-late-ded">{formatPKR(r.late_deduction)}</span>
                        <span className="rp-late-stat-lbl">Deducted</span>
                      </div>
                      <div className="rp-late-stat">
                        <span className="rp-late-leaves">{r.casual_leaves_consumed}</span>
                        <span className="rp-late-stat-lbl">CL Used</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      <div className="rp-footer">
        {reports.length} employees · {MONTHS[month - 1]} {year}
        {cityFilter !== 'All Cities' ? ` · ${cityFilter}` : ''}
      </div>
    </div>
  );
}
